import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Image as ImageIcon, Video, ShoppingBag, Link2, Upload, CheckCircle2, Loader2, ArrowLeft, Rocket, Save, HelpCircle, Trash2 } from "lucide-react";
import UrlPreview from "../components/UrlPreview";
import { toast } from "sonner";

const CTA_DEFAULT = "LEARN_MORE";
const DEFAULT_URL = "https://editsuits.com";

const STEPS = [
  { key: "type", label: "Creative type" },
  { key: "source", label: "Creative" },
  { key: "copy", label: "Copy" },
  { key: "targets", label: "Ad sets" },
  { key: "review", label: "Review" },
];

const CREATIVE_TYPES = [
  { key: "image", label: "Single image", icon: ImageIcon, hint: "Upload a JPG/PNG to Meta's image library" },
  { key: "video", label: "Single video", icon: Video, hint: "Upload a video file (MP4/MOV)" },
  { key: "catalog", label: "Catalog / DPA", icon: ShoppingBag, hint: "Dynamic Product Ad — uses a catalog product set" },
  { key: "existing_post", label: "Use existing post", icon: Link2, hint: "Reuse an existing organic post by its ID" },
];

// Meta-recommended creative specs. The placement-id mapping is what Meta's
// asset_feed_spec engine uses to deliver per-placement creative variants.
const IMAGE_FORMATS = [
  { key: "feed_square",   label: "Feed (Square)",          ratio: "1:1",   pixels: "1080 × 1080",  placements: "Feed, Marketplace, Search" },
  { key: "feed_portrait", label: "Feed (Portrait)",        ratio: "4:5",   pixels: "1080 × 1350",  placements: "Feed, Instagram Feed" },
  { key: "story_reel",    label: "Story / Reel",           ratio: "9:16",  pixels: "1080 × 1920",  placements: "Stories, Reels, IG Stories" },
  { key: "landscape",     label: "Landscape (Right Col.)", ratio: "1.91:1",pixels: "1200 × 628",   placements: "Right Column, Audience Network" },
];
const VIDEO_FORMATS = [
  { key: "feed_square",   label: "Feed (Square)",          ratio: "1:1",   pixels: "1080 × 1080",  placements: "Feed, Marketplace", max: "240s" },
  { key: "feed_vertical", label: "Feed (Vertical)",        ratio: "4:5",   pixels: "1080 × 1350",  placements: "Feed", max: "240s" },
  { key: "story_reel",    label: "Story / Reel",           ratio: "9:16",  pixels: "1080 × 1920",  placements: "Stories, Reels, IG Reels", max: "60s reels / 90s stories" },
  { key: "landscape",     label: "Landscape",              ratio: "16:9",  pixels: "1920 × 1080",  placements: "Right Column, Audience Network", max: "240s" },
];

export default function CreateMetaAd() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftIdFromUrl = searchParams.get("draft");
  const sourceAdFromUrl = searchParams.get("source_ad");
  const sourceAccountFromUrl = searchParams.get("account_id");
  const [step, setStep] = useState(0);

  // Draft state
  const [draftId, setDraftId] = useState(draftIdFromUrl || null);
  const [savingDraft, setSavingDraft] = useState(false);

  // Step 0 — Account + creative type
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [creativeType, setCreativeType] = useState("image");

  // Step 1 — creative source
  // image — single legacy + new multi-format Asset Feed Spec
  const [imageHash, setImageHash] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageAssets, setImageAssets] = useState([]); // {image_hash, image_url, label, format_key, filename}
  const [uploadingImageFormat, setUploadingImageFormat] = useState(null);
  // video — single legacy fields + new multi-format Asset Feed Spec
  const [videoId, setVideoId] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [videoThumbUrl, setVideoThumbUrl] = useState("");
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoAssets, setVideoAssets] = useState([]); // {video_id, label, format_key, thumb_url, filename}
  const [uploadingFormat, setUploadingFormat] = useState(null);
  // step 1 source format hint
  const [imageFormat, setImageFormat] = useState("feed_square");
  const [videoFormat, setVideoFormat] = useState("story_reel");
  // existing post
  const [existingPostId, setExistingPostId] = useState("");
  // catalog
  const [catalogs, setCatalogs] = useState([]);
  const [catalogId, setCatalogId] = useState("");
  const [productSetId, setProductSetId] = useState("");
  // pages / IG
  const [pages, setPages] = useState([]);
  const [pageHints, setPageHints] = useState({});
  const [igAccounts, setIgAccounts] = useState([]);
  const [pageId, setPageId] = useState("");
  const [igActorId, setIgActorId] = useState("");
  // step 2 - copy
  const [adName, setAdName] = useState("");
  const [primaryText, setPrimaryText] = useState("");
  const [headline, setHeadline] = useState("");
  const [linkDescription, setLinkDescription] = useState("");
  const [ctaType, setCtaType] = useState(CTA_DEFAULT);
  const [destinationUrl, setDestinationUrl] = useState(DEFAULT_URL);
  const [ctaTypes, setCtaTypes] = useState([CTA_DEFAULT]);
  const [addUtm, setAddUtm] = useState(true);
  const [addTm, setAddTm] = useState(true);
  // step 3 - targets
  const [adsets, setAdsets] = useState([]);
  const [adsetsLoading, setAdsetsLoading] = useState(false);
  const [selectedAdsetIds, setSelectedAdsetIds] = useState(new Set());
  const [adsetSearch, setAdsetSearch] = useState("");
  const [adsetActiveOnly, setAdsetActiveOnly] = useState(true);
  // step 4 - review/launch
  const [launching, setLaunching] = useState(false);
  const [launchResult, setLaunchResult] = useState(null);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // ---- loaders ----
  useEffect(() => {
    api.get("/accounts").then((r) => {
      const m = (r.data.accounts || []).filter((a) => a.network === "meta" && a.active);
      setAccounts(m);
      if (m[0]) setAccountId(m[0].account_id);
    }).catch(() => toast.error("Failed to load accounts"));
    api.get("/meta/ad-creation/cta-types").then((r) => setCtaTypes(r.data.cta_types || [CTA_DEFAULT])).catch(() => {});
  }, []);

  // load pages/IG/catalogs when account changes
  useEffect(() => {
    if (!accountId) return;
    setPages([]); setIgAccounts([]); setPageId(""); setIgActorId("");
    setCatalogs([]); setCatalogId(""); setProductSetId("");
    api.get("/meta/ad-creation/pages", { params: { account_id: accountId } }).then((r) => {
      const p = r.data.pages || [];
      setPages(p);
      if (p[0]) setPageId(p[0].id);
      setIgAccounts(r.data.instagram_accounts || []);
    }).catch(() => {});
    api.get("/meta/ad-creation/page-hints", { params: { account_id: accountId } })
      .then((r) => {
        const map = {};
        (r.data.hints || []).forEach((h) => { map[h.page_id] = h; });
        setPageHints(map);
      }).catch(() => {});
    api.get("/meta/ad-creation/catalogs", { params: { account_id: accountId } })
      .then((r) => setCatalogs(r.data.catalogs || [])).catch(() => {});
  }, [accountId]);

  // load adsets when step==3 first reached
  useEffect(() => {
    if (step !== 3 || !accountId) return;
    setAdsetsLoading(true);
    api.get("/meta/ad-creation/adsets", { params: { account_id: accountId } })
      .then((r) => setAdsets(r.data.adsets || []))
      .catch(() => toast.error("Failed to load ad sets"))
      .finally(() => setAdsetsLoading(false));
  }, [step, accountId]);

  // ---- step gating ----
  const canAdvance = () => {
    if (step === 0) return !!accountId && !!creativeType;
    if (step === 1) {
      if (creativeType === "image") return imageAssets.length > 0 || !!imageHash;
      if (creativeType === "video") return videoAssets.length > 0 || !!videoId;
      if (creativeType === "existing_post") return !!existingPostId;
      if (creativeType === "catalog") return !!catalogId && !!productSetId;
      return false;
    }
    if (step === 2) {
      if (!adName.trim()) return false;
      if (creativeType !== "existing_post" && !pageId) return false;
      if (creativeType !== "existing_post" && !destinationUrl.trim()) return false;
      return true;
    }
    if (step === 3) return selectedAdsetIds.size > 0;
    return true;
  };

  // ---- uploads ----
  const uploadImageForFormat = async (file, format) => {
    if (!file || !accountId) return;
    setUploadingImageFormat(format.key);
    try {
      const fd = new FormData();
      fd.append("account_id", accountId);
      fd.append("file", file);
      const r = await api.post("/meta/ad-creation/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageAssets((prev) => {
        const filtered = prev.filter((v) => v.format_key !== format.key);
        return [...filtered, {
          image_hash: r.data.hash,
          image_url: r.data.url,
          label: format.label,
          format_key: format.key,
          filename: file.name,
        }];
      });
      toast.success(`${format.label} image uploaded`);
    } catch (e) {
      toast.error(`${format.label} upload failed`, { description: e.response?.data?.detail || e.message });
    } finally { setUploadingImageFormat(null); }
  };

  const removeImageAsset = (formatKey) =>
    setImageAssets((prev) => prev.filter((v) => v.format_key !== formatKey));

  const uploadImage = async () => {
    if (!imageFile || !accountId) return;
    setImageBusy(true);
    try {
      const fd = new FormData();
      fd.append("account_id", accountId);
      fd.append("file", imageFile);
      const r = await api.post("/meta/ad-creation/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageHash(r.data.hash);
      setImageUrl(r.data.url || "");
      toast.success("Image uploaded to Meta library");
    } catch (e) {
      toast.error("Image upload failed", { description: e.response?.data?.detail || e.message });
    } finally { setImageBusy(false); }
  };

  const uploadVideoForFormat = async (file, format) => {
    if (!file || !accountId) return;
    setUploadingFormat(format.key);
    try {
      const fd = new FormData();
      fd.append("account_id", accountId);
      fd.append("file", file);
      const r = await api.post("/meta/ad-creation/upload-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVideoAssets((prev) => {
        // Replace any existing video for this format slot
        const filtered = prev.filter((v) => v.format_key !== format.key);
        return [...filtered, {
          video_id: r.data.video_id,
          label: format.label,
          format_key: format.key,
          filename: file.name,
        }];
      });
      toast.success(`${format.label} video uploaded — Meta is processing it.`);
      // Poll Meta for thumbnail (it appears ~30-60s after upload as encoding finishes)
      const pollThumb = async () => {
        for (let i = 0; i < 6; i++) {
          await new Promise((res) => setTimeout(res, 8000));
          try {
            const st = await api.get("/meta/ad-creation/video-status", {
              params: { video_id: r.data.video_id },
            });
            if (st.data.thumbnail_url) {
              setVideoAssets((prev) =>
                prev.map((v) => v.video_id === r.data.video_id
                  ? { ...v, thumb_url: st.data.thumbnail_url } : v));
              return;
            }
          } catch (e) { /* keep polling */ }
        }
      };
      pollThumb();
    } catch (e) {
      toast.error(`${format.label} upload failed`, { description: e.response?.data?.detail || e.message });
    } finally { setUploadingFormat(null); }
  };

  const removeVideoAsset = (formatKey) =>
    setVideoAssets((prev) => prev.filter((v) => v.format_key !== formatKey));

  const uploadVideo = async () => {
    if (!videoFile || !accountId) return;
    setVideoBusy(true);
    try {
      const fd = new FormData();
      fd.append("account_id", accountId);
      fd.append("file", videoFile);
      const r = await api.post("/meta/ad-creation/upload-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVideoId(r.data.video_id);
      toast.success("Video uploaded — Meta is processing it (this may take a minute)");
    } catch (e) {
      toast.error("Video upload failed", { description: e.response?.data?.detail || e.message });
    } finally { setVideoBusy(false); }
  };

  // ---- launch ----
  const filteredAdsets = useMemo(() => {
    const q = adsetSearch.trim().toLowerCase();
    let out = adsets;
    if (adsetActiveOnly) {
      out = out.filter((a) => (a.effective_status || a.status) === "ACTIVE");
    }
    if (q) {
      out = out.filter((a) =>
        (a.adset_name || "").toLowerCase().includes(q) ||
        (a.campaign_name || "").toLowerCase().includes(q));
    }
    return out;
  }, [adsets, adsetSearch, adsetActiveOnly]);

  const toggleAdset = (id) => {
    setSelectedAdsetIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const selectedAdsets = useMemo(
    () => adsets.filter((a) => selectedAdsetIds.has(a.adset_id)),
    [adsets, selectedAdsetIds],
  );

  // ---- draft load on mount ----
  useEffect(() => {
    if (!draftIdFromUrl) return;
    api.get(`/meta/ad-drafts/${draftIdFromUrl}`).then((r) => {
      const d = r.data;
      setDraftId(d.id);
      setAccountId(d.account_id || "");
      setCreativeType(d.creative_type || "image");
      setImageHash(d.image_hash || "");
      setImageUrl(d.image_url || "");
      setImageAssets(d.image_assets || []);
      setVideoId(d.video_id || "");
      setVideoThumbUrl(d.video_thumb_url || "");
      setVideoAssets(d.video_assets || []);
      setExistingPostId(d.existing_post_id || "");
      setCatalogId(d.catalog_id || "");
      setProductSetId(d.product_set_id || "");
      setPageId(d.page_id || "");
      setIgActorId(d.instagram_actor_id || "");
      setAdName(d.name || "");
      setPrimaryText(d.primary_text || "");
      setHeadline(d.headline || "");
      setLinkDescription(d.link_description || "");
      setCtaType(d.cta_type || CTA_DEFAULT);
      setDestinationUrl(d.destination_url || DEFAULT_URL);
      setAddUtm(d.add_utm !== false);
      setAddTm(d.add_tm_params !== false);
      if (d.adsets?.length) {
        setSelectedAdsetIds(new Set(d.adsets.map((a) => a.adset_id)));
      }
      toast.info(`Loaded draft "${d.name}"`);
    }).catch((e) => toast.error("Failed to load draft", { description: e.response?.data?.detail || e.message }));
  }, [draftIdFromUrl]);

  // ---- duplicate-from-existing-ad: seed the wizard ----
  useEffect(() => {
    if (!sourceAdFromUrl || !sourceAccountFromUrl) return;
    api.get("/meta/ad-duplicate/from-ad", {
      params: { account_id: sourceAccountFromUrl, ad_id: sourceAdFromUrl },
    }).then((r) => {
      const d = r.data;
      setAccountId(d.account_id);
      setCreativeType(d.creative_type || "image");
      setImageHash(d.image_hash || "");
      setVideoId(d.video_id || "");
      setVideoThumbUrl(d.video_thumb_url || "");
      setVideoAssets(d.video_assets || []);
      setExistingPostId(d.existing_post_id || "");
      setPageId(d.page_id || "");
      setIgActorId(d.instagram_actor_id || "");
      setAdName(d.name || "");
      setPrimaryText(d.primary_text || "");
      setHeadline(d.headline || "");
      setLinkDescription(d.link_description || "");
      setCtaType(d.cta_type || CTA_DEFAULT);
      setDestinationUrl(d.destination_url || DEFAULT_URL);
      toast.info(`Duplicated from "${d.source_ad_name}". Tweak and launch.`);
    }).catch((e) => toast.error("Failed to duplicate", { description: e.response?.data?.detail || e.message }));
  }, [sourceAdFromUrl, sourceAccountFromUrl]);

  const buildDraftPayload = () => ({
    id: draftId,
    name: adName.trim() || "Untitled draft",
    account_id: accountId,
    creative_type: creativeType,
    image_hash: imageHash || null,
    image_url: imageUrl || null,
    image_assets: imageAssets.map((v) => ({
      image_hash: v.image_hash, label: v.label, format_key: v.format_key,
      image_url: v.image_url || null, filename: v.filename || null,
    })),
    video_id: videoId || null,
    video_thumb_url: videoThumbUrl || null,
    video_assets: videoAssets.map((v) => ({
      video_id: v.video_id, label: v.label, format_key: v.format_key,
      thumb_url: v.thumb_url || null, filename: v.filename || null,
    })),
    existing_post_id: existingPostId || null,
    catalog_id: catalogId || null,
    product_set_id: productSetId || null,
    page_id: pageId || null,
    instagram_actor_id: igActorId || null,
    primary_text: primaryText || null,
    headline: headline || null,
    link_description: linkDescription || null,
    cta_type: ctaType,
    destination_url: destinationUrl,
    add_utm: addUtm,
    add_tm_params: addTm,
    adsets: selectedAdsets.map((a) => ({
      adset_id: a.adset_id, adset_name: a.adset_name,
      campaign_id: a.campaign_id, campaign_name: a.campaign_name,
    })),
  });

  const saveDraft = async () => {
    if (!accountId) {
      toast.error("Pick an account first");
      return;
    }
    setSavingDraft(true);
    try {
      const r = await api.post("/meta/ad-drafts", buildDraftPayload());
      setDraftId(r.data.draft.id);
      toast.success("Draft saved", { description: `"${r.data.draft.name}" — find it in Meta Ads → Drafts.` });
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally { setSavingDraft(false); }
  };

  const launch = async (initial_status) => {
    setLaunching(true);
    setLaunchResult(null);
    try {
      const payload = {
        account_id: accountId,
        ad_name: adName.trim(),
        creative_type: creativeType,
        image_hash: creativeType === "image" ? (imageHash || null) : null,
        image_assets: creativeType === "image" ? imageAssets.map((v) => ({
          image_hash: v.image_hash, label: v.label, format_key: v.format_key, image_url: v.image_url || null,
        })) : [],
        video_id: creativeType === "video" ? (videoId || null) : null,
        video_thumb_url: creativeType === "video" ? (videoThumbUrl || null) : null,
        video_assets: creativeType === "video" ? videoAssets.map((v) => ({
          video_id: v.video_id, label: v.label, format_key: v.format_key, thumb_url: v.thumb_url || null,
        })) : [],
        existing_post_id: creativeType === "existing_post" ? existingPostId.trim() : null,
        catalog_id: creativeType === "catalog" ? catalogId : null,
        product_set_id: creativeType === "catalog" ? productSetId : null,
        page_id: creativeType !== "existing_post" ? pageId : null,
        instagram_actor_id: igActorId || null,
        primary_text: primaryText || null,
        headline: headline || null,
        link_description: linkDescription || null,
        cta_type: ctaType,
        destination_url: destinationUrl || DEFAULT_URL,
        adsets: selectedAdsets.map((a) => ({
          adset_id: a.adset_id, adset_name: a.adset_name,
          campaign_id: a.campaign_id, campaign_name: a.campaign_name,
        })),
        initial_status,
        add_utm: addUtm,
        add_tm_params: addTm,
      };
      const r = await api.post("/meta/ad-creation/launch", payload);
      setLaunchResult(r.data);
      if (r.data.failure > 0) {
        const devModeHit = (r.data.results || []).some((rr) => (rr.error || "").includes("1885183"));
        const desc = devModeHit
          ? "Your Meta App is in DEVELOPMENT MODE. Open Meta App Dashboard → Settings → flip App Mode to Live, then retry. Or use a creative that references an already-published Page post (creative_type=existing_post)."
          : "Check the activity log for per-ad errors.";
        toast.error(`Launched ${r.data.success}/${r.data.success + r.data.failure} ads — ${r.data.failure} failed`, { description: desc, duration: 12000 });
      } else {
        toast.success(`Launched ${r.data.success} ad${r.data.success === 1 ? "" : "s"} (status=${initial_status})`);
      }
    } catch (e) {
      toast.error("Launch failed", { description: e.response?.data?.detail || e.message });
    } finally { setLaunching(false); }
  };

  // ---- render ----
  return (
    <div data-testid="create-meta-ad-page" className="fade-in">
      <PageHeader
        eyebrow="Meta Ads"
        title="Create a new ad"
        description="Build the creative once, launch it across multiple ad sets. New ads land paused by default for safe review."
        actions={
          <Button variant="outline" onClick={() => navigate("/meta-ads")}
            data-testid="create-ad-back-btn"
            className="rounded-none border-zinc-300 font-mono uppercase tracking-wider text-xs">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Meta Ads
          </Button>
        }
      />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 space-y-6">
        {/* Stepper */}
        <ol className="flex items-center gap-2 flex-wrap" data-testid="create-ad-stepper">
          {STEPS.map((s, i) => {
            const active = i === step;
            const done = i < step;
            return (
              <li key={s.key} className="flex items-center gap-2">
                <button
                  data-testid={`step-${s.key}`}
                  onClick={() => i <= step && setStep(i)}
                  disabled={i > step}
                  className={`h-8 px-3 font-mono text-[11px] uppercase tracking-wider border ${
                    active ? "bg-zinc-900 text-white border-zinc-900"
                    : done ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                    : "bg-white text-zinc-500 border-zinc-200"
                  } ${i > step ? "cursor-not-allowed" : "cursor-pointer hover:border-zinc-400"}`}
                >
                  {i + 1}. {s.label}
                </button>
                {i < STEPS.length - 1 && <div className="w-4 h-px bg-zinc-300" />}
              </li>
            );
          })}
        </ol>

        {/* Step 0 — type */}
        {step === 0 && (
          <div className="bg-white border border-zinc-200 p-6 space-y-6" data-testid="step-type-panel">
            <div>
              <Label className="data-label">Ad account</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger className="rounded-none border-zinc-300 mt-1 max-w-md" data-testid="account-select">
                  <SelectValue placeholder="Pick a Meta account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.account_id} value={a.account_id}>
                      {a.name} <span className="ml-2 text-zinc-500 font-mono text-xs">{a.account_id}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="data-label">Creative type</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {CREATIVE_TYPES.map((t) => {
                  const Icon = t.icon;
                  const selected = creativeType === t.key;
                  return (
                    <button
                      key={t.key}
                      data-testid={`creative-type-${t.key}`}
                      onClick={() => setCreativeType(t.key)}
                      className={`text-left p-4 border ${selected ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={`w-5 h-5 ${selected ? "text-zinc-900" : "text-zinc-500"}`} />
                        <div>
                          <div className="font-semibold text-sm">{t.label}</div>
                          <div className="text-xs text-zinc-500 mt-0.5">{t.hint}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Step 1 — creative source */}
        {step === 1 && (
          <div className="bg-white border border-zinc-200 p-6 space-y-5" data-testid="step-source-panel">
            {creativeType === "image" && (
              <div>
                <Label className="data-label">Per-placement images</Label>
                <p className="text-xs text-zinc-500 mt-1 mb-3">
                  Upload one image per format. Meta delivers the best variant per placement automatically.
                  At least one format is required.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {IMAGE_FORMATS.map((f) => {
                    const uploaded = imageAssets.find((v) => v.format_key === f.key);
                    const busy = uploadingImageFormat === f.key;
                    return (
                      <div key={f.key} data-testid={`image-slot-${f.key}`}
                        className={`p-3 border-2 ${uploaded ? "border-emerald-500 bg-emerald-50" : busy ? "border-zinc-500 bg-zinc-50" : "border-dashed border-zinc-300 bg-white"}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`font-semibold text-sm ${uploaded ? "text-emerald-800" : "text-zinc-900"}`}>{f.label}</span>
                              <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-zinc-900 text-white">{f.ratio}</span>
                              {uploaded && (
                                <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-emerald-600 text-white flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" /> Uploaded
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] font-mono text-zinc-500 mt-0.5">{f.pixels} px</div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">{f.placements}</div>
                          </div>
                        </div>
                        {!uploaded && !busy && (
                          <div className="mt-3">
                            <label className="block">
                              <span className="inline-flex items-center px-3 py-2 bg-zinc-900 text-white text-xs font-mono uppercase tracking-wider cursor-pointer hover:bg-zinc-800">
                                <Upload className="w-3.5 h-3.5 mr-2" /> Upload {f.label}
                              </span>
                              <input type="file" accept="image/*" data-testid={`image-input-${f.key}`}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) uploadImageForFormat(file, f);
                                  e.target.value = "";
                                }}
                                className="hidden" />
                            </label>
                          </div>
                        )}
                        {busy && (
                          <div className="mt-3 text-xs font-mono text-zinc-700 flex items-center gap-1.5">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading {f.label}…
                          </div>
                        )}
                        {uploaded && (
                          <div className="mt-3 flex items-center gap-3">
                            {uploaded.image_url ? (
                              <img src={uploaded.image_url} alt="" className="h-16 w-16 object-cover border border-emerald-300" />
                            ) : (
                              <div className="h-16 w-16 bg-zinc-200 flex items-center justify-center text-zinc-400 border border-emerald-300">
                                <ImageIcon className="w-5 h-5" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-mono text-zinc-800 truncate" title={uploaded.filename}>
                                {uploaded.filename || `hash ${uploaded.image_hash}`}
                              </div>
                              <div className="text-[10px] font-mono text-zinc-500">hash {uploaded.image_hash?.slice(0, 12)}…</div>
                            </div>
                            <button onClick={() => removeImageAsset(f.key)}
                              data-testid={`image-remove-${f.key}`}
                              title={`Remove ${f.label} image`}
                              className="text-zinc-500 hover:text-red-700 p-1">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 text-[11px] font-mono text-zinc-500" data-testid="image-asset-count">
                  {imageAssets.length} of {IMAGE_FORMATS.length} formats uploaded
                </div>
              </div>
            )}
            {creativeType === "video" && (
              <div>
                <Label className="data-label">Per-placement videos</Label>
                <p className="text-xs text-zinc-500 mt-1 mb-3">
                  Upload one video per format. Meta delivers the best variant per placement automatically.
                  At least one format is required — the more you upload, the better Meta's per-placement optimization.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {VIDEO_FORMATS.map((f) => {
                    const uploaded = videoAssets.find((v) => v.format_key === f.key);
                    const busy = uploadingFormat === f.key;
                    return (
                      <div key={f.key} data-testid={`video-slot-${f.key}`}
                        className={`p-3 border-2 ${uploaded ? "border-emerald-500 bg-emerald-50" : busy ? "border-zinc-500 bg-zinc-50" : "border-dashed border-zinc-300 bg-white"}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`font-semibold text-sm ${uploaded ? "text-emerald-800" : "text-zinc-900"}`}>{f.label}</span>
                              <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-zinc-900 text-white">{f.ratio}</span>
                              {uploaded && (
                                <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-emerald-600 text-white flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" /> Uploaded
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] font-mono text-zinc-500 mt-0.5">{f.pixels} px · max {f.max}</div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">{f.placements}</div>
                          </div>
                        </div>
                        {!uploaded && !busy && (
                          <div className="mt-3">
                            <label className="block">
                              <span className="inline-flex items-center px-3 py-2 bg-zinc-900 text-white text-xs font-mono uppercase tracking-wider cursor-pointer hover:bg-zinc-800">
                                <Upload className="w-3.5 h-3.5 mr-2" /> Upload {f.label}
                              </span>
                              <input type="file" accept="video/*" data-testid={`video-input-${f.key}`}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) uploadVideoForFormat(file, f);
                                  e.target.value = "";
                                }}
                                className="hidden" />
                            </label>
                          </div>
                        )}
                        {busy && (
                          <div className="mt-3 text-xs font-mono text-zinc-700 flex items-center gap-1.5">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading {f.label}…
                          </div>
                        )}
                        {uploaded && (
                          <div className="mt-3 flex items-center gap-3">
                            {uploaded.thumb_url ? (
                              <img src={uploaded.thumb_url} alt="" className="h-14 w-14 object-cover border border-emerald-300" />
                            ) : (
                              <div className="h-14 w-14 bg-zinc-200 flex items-center justify-center text-zinc-400 text-[9px] font-mono border border-emerald-300">
                                <Video className="w-5 h-5" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-mono text-zinc-800 truncate" title={uploaded.filename}>
                                {uploaded.filename || `video ${uploaded.video_id}`}
                              </div>
                              <div className="text-[10px] font-mono text-zinc-500">id {uploaded.video_id}</div>
                            </div>
                            <button onClick={() => removeVideoAsset(f.key)}
                              data-testid={`video-remove-${f.key}`}
                              title={`Remove ${f.label} video`}
                              className="text-zinc-500 hover:text-red-700 p-1">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 text-[11px] font-mono text-zinc-500" data-testid="video-asset-count">
                  {videoAssets.length} of {VIDEO_FORMATS.length} formats uploaded
                </div>
              </div>
            )}
            {creativeType === "existing_post" && (
              <div>
                <Label className="data-label">Existing post id</Label>
                <Input value={existingPostId} onChange={(e) => setExistingPostId(e.target.value.trim())} data-testid="existing-post-input"
                  placeholder="100160442403198_836057532722879" className="rounded-none border-zinc-300 mt-1 font-mono text-sm max-w-md" />
                <p className="text-xs text-zinc-500 mt-2">
                  This is the page-post id (format <span className="font-mono">pageId_postId</span>). You can copy it from any existing ad's Effective Story ID.
                </p>
              </div>
            )}
            {creativeType === "catalog" && (
              <div className="space-y-4">
                <div>
                  <Label className="data-label">Catalog</Label>
                  {catalogs.length === 0 ? (
                    <div className="mt-2 space-y-2">
                      <p className="text-xs text-zinc-500">Auto-discovery requires <span className="font-mono">business_management</span> permission. Paste the catalog id and product set id manually:</p>
                      <Input value={catalogId} onChange={(e) => setCatalogId(e.target.value.trim())} data-testid="catalog-id-input"
                        placeholder="Catalog ID" className="rounded-none border-zinc-300 font-mono text-sm max-w-md" />
                      <Input value={productSetId} onChange={(e) => setProductSetId(e.target.value.trim())} data-testid="product-set-input"
                        placeholder="Product Set ID" className="rounded-none border-zinc-300 font-mono text-sm max-w-md" />
                    </div>
                  ) : (
                    <>
                      <Select value={catalogId} onValueChange={setCatalogId}>
                        <SelectTrigger className="rounded-none border-zinc-300 mt-1 max-w-md" data-testid="catalog-select">
                          <SelectValue placeholder="Pick a catalog" />
                        </SelectTrigger>
                        <SelectContent>
                          {catalogs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.id}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="mt-3">
                        <Label className="data-label">Product set</Label>
                        <Select value={productSetId} onValueChange={setProductSetId}>
                          <SelectTrigger className="rounded-none border-zinc-300 mt-1 max-w-md" data-testid="product-set-select">
                            <SelectValue placeholder="Pick a product set" />
                          </SelectTrigger>
                          <SelectContent>
                            {(catalogs.find((c) => c.id === catalogId)?.product_sets || []).map((p) =>
                              <SelectItem key={p.id} value={p.id}>{p.name || p.id}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2 — copy */}
        {step === 2 && (
          <div className="bg-white border border-zinc-200 p-6 space-y-5" data-testid="step-copy-panel">
            <div>
              <Label className="data-label">Ad name</Label>
              <Input value={adName} onChange={(e) => setAdName(e.target.value)} data-testid="ad-name-input"
                placeholder="e.g. 132 Lucas Spring Wedding Suit Video"
                className="rounded-none border-zinc-300 mt-1 font-mono text-sm" />
            </div>
            {creativeType !== "existing_post" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="data-label">Facebook page</Label>
                    <Select value={pageId} onValueChange={setPageId}>
                      <SelectTrigger className="rounded-none border-zinc-300 mt-1" data-testid="page-select">
                        <SelectValue placeholder="Pick a page" />
                      </SelectTrigger>
                      <SelectContent>
                        {pages.map((p) => {
                          const hint = pageHints[p.id];
                          const fallbackName = hint?.sample_ads?.[0];
                          return (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name || (fallbackName ? `Page (used by "${fallbackName.slice(0, 40)}…")` : `Page ${p.id}`)}
                              <span className="ml-2 text-zinc-500 font-mono text-xs">{p.id}</span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {pageId && pageHints[pageId] && !pages.find((p) => p.id === pageId)?.name && (
                      <p className="text-[11px] text-zinc-500 mt-2" data-testid="page-hint">
                        Recent ads on this page: {(pageHints[pageId].sample_ads || []).slice(0, 3).join(" · ")}
                      </p>
                    )}
                    {pages.length === 0 && (
                      <p className="text-xs text-zinc-500 mt-2">No pages auto-discovered. Paste a page id below:</p>
                    )}
                    {pages.length === 0 && (
                      <Input value={pageId} onChange={(e) => setPageId(e.target.value.trim())} data-testid="page-id-input"
                        placeholder="Page ID" className="rounded-none border-zinc-300 mt-2 font-mono text-sm" />
                    )}
                  </div>
                  <div>
                    <Label className="data-label flex items-center gap-1">
                      Instagram account (optional)
                      <span title="The Instagram account that will appear as the author of this ad. Pick one to surface the ad in Instagram placements (Feed, Reels, Stories). Leave as 'None' to run on Facebook placements only." className="cursor-help text-zinc-400 hover:text-zinc-700">
                        <HelpCircle className="w-3.5 h-3.5" />
                      </span>
                    </Label>
                    <Select value={igActorId || "none"} onValueChange={(v) => setIgActorId(v === "none" ? "" : v)}>
                      <SelectTrigger className="rounded-none border-zinc-300 mt-1" data-testid="ig-select">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {igAccounts.map((i) => (
                          <SelectItem key={i.id} value={i.id}>
                            @{i.username || `IG ${i.id}`}
                            <span className="ml-2 text-zinc-500 font-mono text-xs">{i.id}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label className="data-label">Primary text</Label>
                    <Textarea value={primaryText} onChange={(e) => setPrimaryText(e.target.value)} data-testid="primary-text-input"
                      placeholder="Main ad copy that appears above the image…"
                      className="rounded-none border-zinc-300 mt-1 font-mono text-sm" rows={3} />
                  </div>
                  <div>
                    <Label className="data-label">Headline</Label>
                    <Input value={headline} onChange={(e) => setHeadline(e.target.value)} data-testid="headline-input"
                      placeholder="Short headline" className="rounded-none border-zinc-300 mt-1 font-mono text-sm" />
                  </div>
                  <div>
                    <Label className="data-label">Link description</Label>
                    <Input value={linkDescription} onChange={(e) => setLinkDescription(e.target.value)} data-testid="link-description-input"
                      placeholder="Sub-headline below the headline" className="rounded-none border-zinc-300 mt-1 font-mono text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="data-label">CTA button</Label>
                    <Select value={ctaType} onValueChange={setCtaType}>
                      <SelectTrigger className="rounded-none border-zinc-300 mt-1" data-testid="cta-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ctaTypes.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="data-label">Destination URL</Label>
                    <Input value={destinationUrl} onChange={(e) => setDestinationUrl(e.target.value)} data-testid="destination-url-input"
                      placeholder="https://editsuits.com/…" className="rounded-none border-zinc-300 mt-1 font-mono text-sm" />
                  </div>
                </div>
                <UrlPreview baseUrl={destinationUrl} addTm={addTm} addUtm={addUtm} />
              </>
            )}
            <div className="flex gap-6 pt-2 border-t border-zinc-200">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={addTm} onCheckedChange={(v) => setAddTm(!!v)} data-testid="add-tm-checkbox" />
                <span>Auto-append <code className="font-mono text-xs">tmcid</code> + <code className="font-mono text-xs">tmsid</code> for ThoughtMetric</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={addUtm} onCheckedChange={(v) => setAddUtm(!!v)} data-testid="add-utm-checkbox" />
                <span>Auto-append <code className="font-mono text-xs">utm_campaign</code> + <code className="font-mono text-xs">utm_term</code></span>
              </label>
            </div>
          </div>
        )}

        {/* Step 3 — targets */}
        {step === 3 && (
          <div className="bg-white border border-zinc-200 p-6 space-y-4" data-testid="step-targets-panel">
            <div className="flex items-center justify-between">
              <div>
                <div className="data-label">Pick ad sets to launch this ad into</div>
                <div className="text-xs text-zinc-500 mt-1">All ads start <span className="font-mono">PAUSED</span> by default — you can flip to ACTIVE on the review screen.</div>
              </div>
              <Input value={adsetSearch} onChange={(e) => setAdsetSearch(e.target.value)} data-testid="adset-search-input"
                placeholder="Filter by adset/campaign name…" className="rounded-none border-zinc-300 font-mono text-sm w-72" />
            </div>
            <div className="flex items-center gap-3 text-xs font-mono">
              <button onClick={() => setSelectedAdsetIds(new Set(filteredAdsets.map((a) => a.adset_id)))}
                data-testid="select-all-adsets" className="hover:underline uppercase">select all visible</button>
              <span className="text-zinc-300">·</span>
              <button onClick={() => setSelectedAdsetIds(new Set())}
                data-testid="clear-adsets" className="hover:underline uppercase">clear</button>
              <label className="ml-3 flex items-center gap-1.5 cursor-pointer">
                <Checkbox checked={adsetActiveOnly} onCheckedChange={(v) => setAdsetActiveOnly(!!v)} data-testid="adset-active-only" />
                <span className="uppercase">active only</span>
              </label>
              <span className="ml-auto text-zinc-600">{selectedAdsetIds.size} selected · {filteredAdsets.length} visible</span>
            </div>
            <div className="border border-zinc-200 max-h-[420px] overflow-auto">
              {adsetsLoading && <div className="p-4 text-sm text-zinc-500 font-mono">Loading ad sets…</div>}
              {!adsetsLoading && adsets.length === 0 && (
                <div className="p-4 text-sm text-zinc-500 font-mono" data-testid="adset-empty-account">
                  This account has no ad sets at all. Did you pick the right ad account on step 1?
                </div>
              )}
              {!adsetsLoading && adsets.length > 0 && filteredAdsets.length === 0 && (
                <div className="p-4 text-sm text-zinc-600 font-mono" data-testid="adset-empty-filtered">
                  No ad sets match the current filters.
                  {adsetActiveOnly && (
                    <button onClick={() => setAdsetActiveOnly(false)} data-testid="show-all-adsets"
                      className="ml-2 underline hover:no-underline text-zinc-900">
                      Show all {adsets.length} (incl. paused)
                    </button>
                  )}
                </div>
              )}
              <ul>
                {filteredAdsets.map((a) => {
                  const looksDynamic = a.is_dynamic_creative
                    || /advantage\+/i.test(a.adset_name || "")
                    || /advantage\+/i.test(a.campaign_name || "");
                  return (
                  <li key={a.adset_id} className="border-b border-zinc-100 last:border-0">
                    <label className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 cursor-pointer">
                      <Checkbox checked={selectedAdsetIds.has(a.adset_id)} onCheckedChange={() => toggleAdset(a.adset_id)}
                        data-testid={`adset-pick-${a.adset_id}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate flex items-center gap-2">
                          <span className="truncate">{a.adset_name}</span>
                          {looksDynamic && (
                            <span title="Looks like a Dynamic Creative / Advantage+ ad-set — Meta allows only ONE active ad. Pause/delete the existing ad first or pick another ad-set."
                              className="px-1 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300">
                              dynamic / Adv+
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 font-mono truncate">
                          {a.campaign_name || "—"} · {a.effective_status || a.status}
                        </div>
                      </div>
                      <div className="text-[10px] text-zinc-400 font-mono">{a.adset_id}</div>
                    </label>
                  </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {/* Step 4 — review */}
        {step === 4 && (
          <div className="bg-white border border-zinc-200 p-6 space-y-5" data-testid="step-review-panel">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3 text-sm">
                <div><span className="data-label block mb-1">Ad name</span><span className="font-mono">{adName}</span></div>
                <div><span className="data-label block mb-1">Creative type</span><span className="font-mono">{creativeType}</span></div>
                {creativeType === "image" && (
                  <div>
                    <span className="data-label block mb-1">Images</span>
                    {imageAssets.length > 0 ? (
                      <ul className="text-xs font-mono space-y-0.5" data-testid="review-image-assets">
                        {imageAssets.map((v) => (
                          <li key={v.format_key}>• {v.label}: <span className="text-zinc-500">{v.image_hash?.slice(0, 12)}…</span></li>
                        ))}
                      </ul>
                    ) : (
                      <>
                        {imageUrl && <img src={imageUrl} alt="preview" className="max-h-40 border border-zinc-200" />}
                        <span className="font-mono text-xs">{imageHash}</span>
                      </>
                    )}
                  </div>
                )}
                {creativeType === "video" && (
                  <div>
                    <span className="data-label block mb-1">Videos</span>
                    {videoAssets.length > 0 ? (
                      <ul className="text-xs font-mono space-y-0.5" data-testid="review-video-assets">
                        {videoAssets.map((v) => (
                          <li key={v.format_key}>• {v.label}: <span className="text-zinc-500">{v.video_id}</span></li>
                        ))}
                      </ul>
                    ) : (
                      <span className="font-mono text-xs">{videoId}</span>
                    )}
                  </div>
                )}
                {creativeType === "existing_post" && (
                  <div><span className="data-label block mb-1">Post id</span><span className="font-mono text-xs">{existingPostId}</span></div>
                )}
                {creativeType === "catalog" && (
                  <div>
                    <span className="data-label block mb-1">Catalog / product set</span>
                    <span className="font-mono text-xs">{catalogId} / {productSetId}</span>
                  </div>
                )}
                {creativeType !== "existing_post" && (
                  <>
                    <div><span className="data-label block mb-1">Headline</span><span className="text-sm">{headline || "—"}</span></div>
                    <div><span className="data-label block mb-1">Primary text</span><span className="text-sm whitespace-pre-wrap">{primaryText || "—"}</span></div>
                    <div><span className="data-label block mb-1">CTA · Destination</span><span className="font-mono text-xs">{ctaType} · {destinationUrl}</span></div>
                  </>
                )}
                <div><span className="data-label block mb-1">URL params</span>
                  <span className="text-xs">{addTm ? "✓ tm" : "✗ tm"} · {addUtm ? "✓ utm" : "✗ utm"}</span>
                </div>
              </div>
              <div className="border border-zinc-200 p-4 bg-zinc-50">
                <div className="data-label mb-2">Launching to {selectedAdsets.length} ad set{selectedAdsets.length === 1 ? "" : "s"}</div>
                <ul className="text-xs space-y-1 max-h-72 overflow-auto" data-testid="review-adsets-list">
                  {selectedAdsets.map((a) => (
                    <li key={a.adset_id} className="font-mono">• {a.adset_name} <span className="text-zinc-500">({a.campaign_name})</span></li>
                  ))}
                </ul>
              </div>
            </div>
            {launchResult && (
              <div className={`p-4 border ${launchResult.failure > 0 ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"}`}
                   data-testid="launch-result">
                <div className="font-mono text-sm">
                  {launchResult.success} created · {launchResult.failure} failed · status={launchResult.initial_status}
                </div>
                {(launchResult.results || []).some((r) => (r.error || "").includes("1885183")) && (
                  <div className="mt-2 p-3 bg-amber-100 border border-amber-300 text-xs" data-testid="dev-mode-hint">
                    <div className="font-semibold uppercase tracking-wider text-amber-900 text-[10px] mb-1">⚠ Meta App is in Development Mode</div>
                    <div className="text-amber-900">
                      Meta auto-creates a hidden "creative post" on your Page that inherits your App's mode. While the app is in <span className="font-mono">Development</span>, those posts can't run as ads.
                      <div className="mt-1">
                        <span className="font-semibold">Fix:</span> go to <a className="underline" href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer">Meta App Dashboard</a> → your app → top toggle from <span className="font-mono">Development</span> to <span className="font-mono">Live</span>, then retry. (Alternative: change the creative type to <span className="font-mono">existing_post</span> and use a Page post you've already published.)
                      </div>
                    </div>
                  </div>
                )}
                <ul className="text-xs font-mono mt-2 space-y-1 max-h-40 overflow-auto">
                  {launchResult.results.map((r, i) => (
                    <li key={i} className={r.ok ? "text-emerald-800" : "text-red-700"}>
                      {r.ok ? "✓" : "✗"} {r.adset_name} {r.ok ? `→ ad ${r.ad_id}` : ""}
                      {!r.ok && r.hint && (
                        <div className="mt-1 ml-4 px-2 py-1 bg-amber-100 border border-amber-300 text-amber-900 text-[11px] font-sans normal-case">
                          {r.hint}
                        </div>
                      )}
                      {!r.ok && !r.hint && r.error && (
                        <div className="mt-1 ml-4 text-[10px] text-red-700 break-all">{r.error}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-3 pt-2 border-t border-zinc-200">
              <Button onClick={() => launch("PAUSED")} disabled={launching} data-testid="launch-paused-btn"
                className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-wider text-xs">
                {launching ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Launching…</> : <><Rocket className="w-4 h-4 mr-2" /> Launch paused</>}
              </Button>
              <Button onClick={() => launch("ACTIVE")} disabled={launching} variant="outline"
                data-testid="launch-active-btn"
                className="rounded-none border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-mono uppercase tracking-wider text-xs">
                {launching ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Launching…</> : <><Rocket className="w-4 h-4 mr-2" /> Launch active</>}
              </Button>
            </div>
          </div>
        )}

        {/* Nav */}
        <div className="flex items-center justify-between pt-2 gap-3 flex-wrap">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}
            data-testid="step-prev-btn"
            className="rounded-none border-zinc-300 font-mono uppercase tracking-wider text-xs">Back</Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={saveDraft} disabled={savingDraft || !accountId}
              data-testid="save-draft-btn"
              className="rounded-none border-zinc-300 font-mono uppercase tracking-wider text-xs">
              {savingDraft ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save draft</>}
            </Button>
            {step < STEPS.length - 1 && (
              <Button disabled={!canAdvance()} onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                data-testid="step-next-btn"
                className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-wider text-xs">
                Next step
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
